import { observer } from 'mobx-react-lite';

import { composeStore } from 'owa-mail-compose-store';
import { FullCompose } from 'owa-mail-compose-view';
import { lazyResetFocus } from 'owa-mail-focus-manager';
import getConversationReadingPaneViewState from 'owa-mail-reading-pane-store/lib/utils/getConversationReadingPaneViewState';
import { ConversationReadingPane, ItemReadingPane } from 'owa-mail-reading-pane-view';
import { SxSLayerView } from 'owa-sxs';
import {
    EditMode,
    lazyOnBeforeImageGalleryItemSelected,
    lazyOnChangeByImageGallery,
    lazySxSStoreModule,
    lazyTryCloseSxS,
    SxSCustomization,
    SxSEventNames,
    SxSReadingPaneMode,
    sxsStore,
} from 'owa-sxs-store';
import getExtraCustomDataForDatapoint from 'owa-sxs-store/lib/utils/getExtraCustomDataForDatapoint';
import { ImageGalleryItem } from 'owa-sxsdata';
import * as React from 'react';
import { render } from 'react-dom';
import getCommandBarButtons from './commandBar/getCommandBarButtons';
import SxSCustomView from './SxSCustomView';

const styles = require('./SxSV2.scss');

const SxSV2 = observer(function SxSV2(props: {}) {
    const rightHandSideHtml = React.useRef<string>(null);
    const rightHandSideContainerDiv = React.useRef<HTMLDivElement>(null);
    React.useEffect(
        () => {
            const readingPaneState = sxsStore.readingPaneState;
            if (
                readingPaneState &&
                readingPaneState.mode === SxSReadingPaneMode.ConversationReadingPane &&
                getConversationReadingPaneViewState()?.conversationId.Id !==
                    readingPaneState.conversationId.Id
            ) {
                lazyTryCloseSxS.importAndExecute('conversationChanged');
            }
        }
        // No dependency array, so it runs every render
    );
    const renderRightHandSideFake = () => {
        /* tslint:disable:react-no-dangerous-html */
        return rightHandSideHtml.current ? (
            <div
                className={styles.rightHandSideContainer}
                dangerouslySetInnerHTML={{ __html: rightHandSideHtml.current }}
            />
        ) : null;
        /* tslint:enable:react-no-dangerous-html */
    };
    const renderRightHandSide = () => {
        const rightHandSide = renderRightHandSideInternal();
        if (rightHandSide) {
            return (
                <div
                    className={styles.rightHandSideContainer}
                    ref={r => (rightHandSideContainerDiv.current = r)}>
                    {rightHandSide}
                </div>
            );
        } else {
            return null;
        }
    };
    if (!sxsStore.shouldShow) {
        return null;
    }
    if (sxsStore.shouldUseFakeRightHandSide) {
        if (!rightHandSideHtml.current && rightHandSideContainerDiv.current) {
            rightHandSideHtml.current = rightHandSideContainerDiv.current.innerHTML;
        }
    } else {
        rightHandSideHtml.current = null;
    }
    const useFakeRightHandSide =
        sxsStore.shouldUseFakeRightHandSide && sxsStore.isUsingFakeRightHandSide;
    const hideOwaHeader =
        sxsStore.customization === SxSCustomization.FilesHub ||
        sxsStore.customization === SxSCustomization.DeepLink;
    const hideCloseButton = sxsStore.customization === SxSCustomization.DeepLink;
    const modal = sxsStore.customization !== SxSCustomization.DeepLink;
    const showPopOutButton =
        sxsStore.editState.itemId &&
        sxsStore.extendedViewState.attachmentId &&
        (sxsStore.customization === SxSCustomization.Mail ||
            sxsStore.customization === SxSCustomization.FilesHub);
    return (
        <SxSLayerView
            commandBarButtons={getCommandBarButtons(sxsStore)}
            getExtraCustomDataForDatapoint={getExtraCustomDataForDatapoint}
            getItemIdForLogging={getItemIdForLogging}
            hideCloseButton={hideCloseButton}
            hideOwaHeader={hideOwaHeader}
            modal={modal}
            newStyle={true}
            onAnimationEnd={onAnimationEnd}
            onBeforeImageGalleryItemSelected={onBeforeImageGalleryItemSelected}
            onClose={closeSxS}
            onError={onError}
            onFocusRightHandPane={lazyResetFocus.importAndExecute}
            onImageGalleryItemSelected={onImageGalleryItemSelected}
            onLoad={onLoad}
            onPopOut={showPopOutButton ? popOutButtonClicked : null}
            renderCustomView={renderSxSCustomPreview}
            shouldHide={sxsStore.isInvisible}
            useCommandBarStyleUpdate={true}
            viewState={sxsStore.viewState}>
            {useFakeRightHandSide ? renderRightHandSideFake() : renderRightHandSide()}
        </SxSLayerView>
    );
});

let sxsV2InsertedToDom = false;
export default function insertSxSV2IntoDomIfNeeded() {
    if (sxsV2InsertedToDom) {
        return;
    }

    const containerDiv = document.createElement('div');
    containerDiv.id = 'owaSxSV2';
    document.body.appendChild(containerDiv);
    render(<SxSV2 />, containerDiv);
    sxsV2InsertedToDom = true;
}

async function onBeforeImageGalleryItemSelected(imageGalleryItem: ImageGalleryItem) {
    const onBeforeImageGalleryItemSelected = await lazyOnBeforeImageGalleryItemSelected.import();
    await onBeforeImageGalleryItemSelected(imageGalleryItem);
}

function onAnimationEnd() {
    sxsStore.eventManager.emit(SxSEventNames.AnimationEndEvent);
}

function onLoad(loadedTime?: number) {
    sxsStore.eventManager.emit(SxSEventNames.PreviewLoadEvent, { loadedTime });
    sxsStore.eventManager.emit(SxSEventNames.PreviewLoadOrErrorEvent, { loadedTime });
}

function onError(errorMessage: string, error?: Error) {
    sxsStore.eventManager.emit(SxSEventNames.PreviewErrorEvent, { errorMessage, error });
    sxsStore.eventManager.emit(SxSEventNames.PreviewLoadOrErrorEvent, { errorMessage, error });
}

async function closeSxS() {
    const tryCloseSxS = await lazyTryCloseSxS.import();
    tryCloseSxS('closeButton');
}

async function onImageGalleryItemSelected() {
    const onChangeByImageGallery = await lazyOnChangeByImageGallery.import();
    onChangeByImageGallery();
}

function renderRightHandSideInternal() {
    const composeViewState = sxsStore.composeId && composeStore.viewStates.get(sxsStore.composeId);
    if (composeViewState) {
        return <FullCompose viewState={composeViewState} />;
    }
    const readingPaneState = sxsStore.readingPaneState;
    if (readingPaneState) {
        if (
            readingPaneState.mode === SxSReadingPaneMode.ConversationReadingPane &&
            getConversationReadingPaneViewState()?.conversationId.Id ===
                readingPaneState.conversationId.Id
        ) {
            return (
                <ConversationReadingPane
                    conversationId={readingPaneState.conversationId}
                    conversationSubject={readingPaneState.conversationSubject}
                    conversationCategories={readingPaneState.conversationCategories}
                    shouldNotFocusOnInitialRender={true}
                    instrumentationContext={
                        getConversationReadingPaneViewState(readingPaneState.conversationId.Id)
                            .instrumentationContext
                    }
                />
            );
        } else if (readingPaneState.mode === SxSReadingPaneMode.ItemReadingPane) {
            return (
                <ItemReadingPane
                    itemId={readingPaneState.itemId}
                    itemReadingPaneViewState={readingPaneState.itemReadingPaneViewState}
                />
            );
        }
    }
    return null;
}

async function popOutButtonClicked() {
    const sxsStoreModule = await lazySxSStoreModule.import();
    sxsStoreModule.openSxSInANewWindow(
        sxsStore.editState.itemId,
        sxsStore.extendedViewState.attachmentId ? sxsStore.extendedViewState.attachmentId.Id : null,
        sxsStore.editState.mode === EditMode.Edit
    );
    sxsStoreModule.tryCloseSxS('popOutButton');
}

function renderSxSCustomPreview() {
    return (
        <SxSCustomView
            onError={onError}
            onLoad={onLoad}
            viewState={sxsStore.extendedViewState}
            customPreviewState={sxsStore.customPreviewState}
        />
    );
}

function getItemIdForLogging() {
    return sxsStore.editState?.itemId?.Id;
}
